import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Form,
  Modal,
  Alert,
  InputGroup,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { subscriptionAPI } from "../../api/subscription";
import Swal from "sweetalert2";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaSearch,
  FaCog,
  FaTag,
  FaCalendarAlt,
} from "react-icons/fa";

const SubscriptionTypes = () => {
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [subscriptionPeriods, setSubscriptionPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");

  // Form states
  const [typeForm, setTypeForm] = useState({
    name: "",
  });

  const [periodForm, setPeriodForm] = useState({
    months: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesResponse, periodsResponse] = await Promise.all([
        subscriptionAPI.getSubscriptionTypes(),
        subscriptionAPI.getSubscriptionPeriods(),
      ]);

      setSubscriptionTypes(typesResponse.data.data || typesResponse.data);
      setSubscriptionPeriods(periodsResponse.data.data || periodsResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load subscription data");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load subscription data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (e) => {
    setTypeForm({
      ...typeForm,
      [e.target.name]: e.target.value,
    });
  };

  const handlePeriodChange = (e) => {
    setPeriodForm({
      ...periodForm,
      [e.target.name]: e.target.value,
    });
  };

  const validateTypeForm = () => {
    if (!typeForm.name.trim()) {
      return "Type name is required";
    }
    return null;
  };

  const validatePeriodForm = () => {
    if (!periodForm.months || parseInt(periodForm.months) <= 0) {
      return "Valid number of months is required";
    }
    return null;
  };

  const handleTypeSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateTypeForm();
    if (validationError) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: validationError,
      });
      return;
    }

    try {
      if (editingType) {
        await subscriptionAPI.updateSubscriptionType(editingType.id, typeForm);
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Subscription type updated successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await subscriptionAPI.createSubscriptionType(typeForm);
        Swal.fire({
          icon: "success",
          title: "Created!",
          text: "New subscription type created.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      fetchData();
      resetTypeForm();
      setShowTypeModal(false);
    } catch (error) {
      console.error("Error submitting type:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Operation failed",
      });
    }
  };

  const handlePeriodSubmit = async (e) => {
    e.preventDefault();

    const validationError = validatePeriodForm();
    if (validationError) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: validationError,
      });
      return;
    }

    try {
      if (editingPeriod) {
        await subscriptionAPI.updateSubscriptionPeriod(
          editingPeriod.id,
          periodForm
        );
        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Subscription period updated successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await subscriptionAPI.createSubscriptionPeriod(periodForm);
        Swal.fire({
          icon: "success",
          title: "Created!",
          text: "New subscription period created.",
          timer: 1500,
          showConfirmButton: false,
        });
      }

      fetchData();
      resetPeriodForm();
      setShowPeriodModal(false);
    } catch (error) {
      console.error("Error submitting period:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Operation failed",
      });
    }
  };

  const handleTypeDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await subscriptionAPI.deleteSubscriptionType(id);
        Swal.fire("Deleted!", "Subscription type has been deleted.", "success");
        fetchData();
      } catch (error) {
        console.error("Error deleting type:", error);
        Swal.fire(
          "Error!",
          error.response?.data?.message ||
            "Failed to delete subscription type.",
          "error"
        );
      }
    }
  };

  const handlePeriodDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await subscriptionAPI.deleteSubscriptionPeriod(id);
        Swal.fire(
          "Deleted!",
          "Subscription period has been deleted.",
          "success"
        );
        fetchData();
      } catch (error) {
        console.error("Error deleting period:", error);
        Swal.fire(
          "Error!",
          error.response?.data?.message ||
            "Failed to delete subscription period.",
          "error"
        );
      }
    }
  };

  const resetTypeForm = () => {
    setTypeForm({
      name: "",
    });
    setEditingType(null);
  };

  const resetPeriodForm = () => {
    setPeriodForm({
      months: "",
    });
    setEditingPeriod(null);
  };

  const openTypeModal = (type = null) => {
    if (type) {
      setEditingType(type);
      setTypeForm({
        name: type.name || "",
      });
    } else {
      resetTypeForm();
    }
    setShowTypeModal(true);
  };

  const openPeriodModal = (period = null) => {
    if (period) {
      setEditingPeriod(period);
      setPeriodForm({
        months: period.months || "",
      });
    } else {
      resetPeriodForm();
    }
    setShowPeriodModal(true);
  };

  const filteredTypes = subscriptionTypes.filter((type) =>
    type.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPeriods = subscriptionPeriods.filter((period) =>
    period.months.toString().includes(searchTerm)
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-settings-page">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-0">Subscription Settings</h2>
            <p className="text-muted mb-0">
              Manage subscription types and periods
            </p>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center bg-primary text-white">
              <h5 className="mb-0">
                <FaTag className="me-2" /> Subscription Types
              </h5>
              <Button variant="light" size="sm" onClick={() => openTypeModal()}>
                <FaPlus className="me-1" /> Add Type
              </Button>
            </Card.Header>
            <Card.Body>
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search subscription types..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>

              <div className="table-responsive">
                <Table hover striped>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTypes.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <div className="text-muted">
                            <FaTag size={32} className="mb-2" />
                            <p>No subscription types found</p>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => openTypeModal()}
                            >
                              <FaPlus className="me-1" /> Add First Type
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredTypes.map((type) => (
                        <tr key={type.id}>
                          <td>
                            <Badge bg="secondary">#{type.id}</Badge>
                          </td>
                          <td>
                            <div className="fw-medium">{type.name}</div>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatDate(type.created_at)}
                            </small>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => openTypeModal(type)}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleTypeDelete(type.id)}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="text-muted">
              Total Types: {subscriptionTypes.length}
            </Card.Footer>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center bg-success text-white">
              <h5 className="mb-0">
                <FaCalendarAlt className="me-2" /> Subscription Periods
              </h5>
              <Button
                variant="light"
                size="sm"
                onClick={() => openPeriodModal()}
              >
                <FaPlus className="me-1" /> Add Period
              </Button>
            </Card.Header>
            <Card.Body>
              <div className="table-responsive">
                <Table hover striped>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Duration</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptionPeriods.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4">
                          <div className="text-muted">
                            <FaCalendarAlt size={32} className="mb-2" />
                            <p>No subscription periods found</p>
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => openPeriodModal()}
                            >
                              <FaPlus className="me-1" /> Add First Period
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      subscriptionPeriods.map((period) => (
                        <tr key={period.id}>
                          <td>
                            <Badge bg="secondary">#{period.id}</Badge>
                          </td>
                          <td>
                            <div className="fw-medium">
                              {period.months} Month
                              {period.months !== 1 ? "s" : ""}
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatDate(period.created_at)}
                            </small>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => openPeriodModal(period)}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handlePeriodDelete(period.id)}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="text-muted">
              Total Periods: {subscriptionPeriods.length}
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Subscription Type Modal */}
      <Modal
        show={showTypeModal}
        onHide={() => {
          setShowTypeModal(false);
          resetTypeForm();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingType
              ? "Edit Subscription Type"
              : "Add New Subscription Type"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleTypeSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Type Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={typeForm.name}
                onChange={handleTypeChange}
                placeholder="e.g., Basic, Premium, Gold, Gym"
                required
                autoFocus
              />
              <Form.Text className="text-muted">
                Enter a descriptive name for the subscription type
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowTypeModal(false);
                resetTypeForm();
              }}
            >
              <FaTimes className="me-1" /> Cancel
            </Button>
            <Button variant="primary" type="submit">
              <FaSave className="me-1" />
              {editingType ? "Update Type" : "Create Type"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Subscription Period Modal */}
      <Modal
        show={showPeriodModal}
        onHide={() => {
          setShowPeriodModal(false);
          resetPeriodForm();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingPeriod
              ? "Edit Subscription Period"
              : "Add New Subscription Period"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePeriodSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Duration (Months) *</Form.Label>
              <Form.Control
                type="number"
                name="months"
                value={periodForm.months}
                onChange={handlePeriodChange}
                placeholder="e.g., 1, 3, 6, 12"
                min="1"
                max="36"
                required
                autoFocus
              />
              <Form.Text className="text-muted">
                Number of months for this subscription period
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPeriodModal(false);
                resetPeriodForm();
              }}
            >
              <FaTimes className="me-1" /> Cancel
            </Button>
            <Button variant="success" type="submit">
              <FaSave className="me-1" />
              {editingPeriod ? "Update Period" : "Create Period"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionTypes;
